///<summary>Chart Coloring provides Color models and defines Palettes for use in charting.</summary >
/// <amd-module name="Tfs/Charting/ChartColor" />

import * as Contracts from "VSS/Features/Charts/Contracts";
import { ColorUtilities } from "VSS/Features/Charts/Internal/ColorUtilities";
import { DefaultFeatureColorProvider, DefaultColorDictionary } from "VSS/Features/Charts/Internal/DefaultColorDictionary";

import { ChartProviders } from "Tfs/Charting/ChartProviders";
import { ColorConfiguration } from "Tfs/Charting/Contracts/Reporting";
import * as Resources from "Tfs/Charting/Resources";

export class TestResultFeatureColorProvider extends DefaultFeatureColorProvider {
    public getFeatureColor(key: string, isSubdued?: boolean): Contracts.ColorPair {
        let backgroundColor: string | undefined = undefined;

        //TCM specific color mappings to provide the semantic coloring for test execution outcome.
        switch (key) {
            case Resources.ChartDimensionValue_Passed:// Green
                backgroundColor = (isSubdued) ? "#8DC54B" : "#339947";
                break;

            case Resources.ChartDimensionValue_Failed: // Red
                backgroundColor = (isSubdued) ? "#F58B1F" : "#E31E26";
                break;

            case Resources.ChartDimensionValue_Blocked: // Gray
                backgroundColor = (isSubdued) ? "#747475" : "#525151";
                break;

            case Resources.ChartDimensionValue_NotApplicable: // Yellow
                backgroundColor = (isSubdued) ? "#FAEF67" : "#FFCC05";
                break;

            case Resources.ChartDimensionValue_NotRun: // Blue
                backgroundColor = (isSubdued) ? "#3673B8" : "#292E6B";
                break;
        }

        if (backgroundColor) {
            return {
                background: backgroundColor,
                foreground: ColorUtilities.selectForegroundColor(backgroundColor)
            };
        } else {
            return super.getFeatureColor(key, isSubdued);
        }
    }
}

/** Selects the appropriate color provider, given the feature */
export function getFeatureColorProvider(scope: string): Contracts.FeatureColorProvider {
    if (scope === ChartProviders.testReports || scope === ChartProviders.testRunSummary) {
        return new TestResultFeatureColorProvider();
    } else {
        return new DefaultFeatureColorProvider();
    }
}

/** Manages Colors of Charts */
export namespace ColorDictionary {
    /** Creates a new color manager from a chart configuration */
    export function fromColorEntries(
        featureColorProvider: Contracts.FeatureColorProvider,
        userColors?: ColorConfiguration[],
        contractVersion?: number): Contracts.ColorDictionary {

        var instance: Contracts.ColorDictionary;

        // Null version is used from WIT Charts Page
        // Version 1 is an un-migrated Chart Id, which is only viewable on dashboard
        // Version 2 is used by a pinned chart from feature page, which is viewable on Dashboard
        // Version 3 is used by Charting widgets which have no reference to chart storage.
        if (!contractVersion || (contractVersion >= 1 && contractVersion <= 3)) {
            instance = new DefaultColorDictionary(userColors, featureColorProvider);
        } else {
            throw "ColorDictionary only supports version levels 1-3";
        }

        return instance;
    }
}

/** Model of pending edits prepared under the current Configuration. */
export class ColorEditingModel {

    private _featureColorProvider: Contracts.FeatureColorProvider;
    private _currentDimension: string;
    private _colorDictionaries: { [dimension: string]: Contracts.ColorDictionary };

    constructor(dimension: string, initialColorDictionary: Contracts.ColorDictionary, featureColorProvider: Contracts.FeatureColorProvider) {
        this._currentDimension = dimension;
        this._colorDictionaries = {};
        this._colorDictionaries[this._currentDimension] = initialColorDictionary;
        this._featureColorProvider = featureColorProvider;
    }

    /** Changes the pending color dictionary to the specified dimension. Edits on previous dimension are still available while model exists. */
    public changeColoringDimension(dimension: string): Contracts.ColorDictionary {

        this._currentDimension = dimension;
        if (!this._colorDictionaries[this._currentDimension]) {
            this._colorDictionaries[this._currentDimension] = new DefaultColorDictionary(/* user colors */undefined, this._featureColorProvider);
        }

        return this._colorDictionaries[this._currentDimension];
    }

    /** Returns a color manager associated with the current chart dimension. */
    public getCurrentColorDictionary(): Contracts.ColorDictionary {
        return this._colorDictionaries[this._currentDimension];
    }

    public getCurrentDimension(): string {
        return this._currentDimension;
    }
}

/** Abstraction for a chart which supports use of a color picker. */
export interface IColorPickableChart {
    getColorDictionary(): Contracts.ColorDictionary;
    repaintOnColorChange(): void;
}